import { COLORS } from '@src/constants/design-tokens/colors';

const PRIMARY_MAIN = COLORS.primary.main;
const PRIMARY_LIGHT = COLORS.primary.light;

/**
 * ビジュアルエフェクト
 * シャドウ、グラデーション、アニメーションなどの視覚効果の定義
 *
 * このファイルには以下のカテゴリが含まれます：
 * - シャドウ: ボックスシャドウとグロー効果
 * - グラデーション: 線形グラデーションの定義
 * - アニメーション: トランジションとイージングの設定
 */

/**
 * シャドウ - 洗練された影の効果
 * UIコンポーネントに深さと階層を与えるための影の定義
 */
export const SHADOWS = {
  // 基本的なシャドウ
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

  // カラーシャドウ
  primary: '0 4px 14px 0 rgba(25, 118, 210, 0.25)',
  secondary: '0 4px 14px 0 rgba(233, 30, 99, 0.25)',

  // インナーシャドウ
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

  // グロー効果
  glow: '0 0 0 1px rgba(25, 118, 210, 0.3), 0 0 20px rgba(25, 118, 210, 0.2)',
} as const;

/**
 * グラデーション - 美しいグラデーション効果
 * 背景やオーバーレイに使用するグラデーションの定義
 */
export const GRADIENTS = {
  // プライマリグラデーション
  primary: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_LIGHT} 100%)`,
  primarySubtle: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',

  // セカンダリグラデーション
  secondary: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
  secondarySubtle: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',

  // ニュートラルグラデーション
  neutral: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
  neutralDark: 'linear-gradient(135deg, #616161 0%, #424242 100%)',

  // セマンティックグラデーション
  success: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
  warning: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
  error: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)',
  info: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',

  // 特別な効果
  rainbow: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  sunset: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)',
  ocean: 'linear-gradient(135deg, #667db6 0%, #0082c8 35%, #0082c8 100%)',
} as const;

/**
 * アニメーション - 滑らかで自然なアニメーション
 * UIのトランジションとイージング関数の定義
 */
export const ANIMATION = {
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '250ms',
    slow: '350ms',
    slower: '500ms',
    slowest: '750ms',
  },

  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',

    // カスタムイージング
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    elastic: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    smooth: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },

  // プリセットトランジション
  transitions: {
    all: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    colors:
      'color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    shadow: 'box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    transform: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;
